import axios from 'axios';
import { apiUrl } from '../config/api';

/**
 * Open a stored document without downloading it first.
 *
 * The old approach fetched the whole file, built a Blob and opened that, so
 * nothing appeared until the last byte arrived and the browser could not cache
 * it. Instead we ask the API for a short-lived signed URL and hand it straight
 * to the browser, which streams the PDF progressively and caches it (the URL
 * carries the document version, so a replaced document gets a fresh URL).
 *
 * @param {number|string} documentId
 * @param {{ download?: boolean }} [options]
 * @returns {Promise<boolean>} true when a viewer was opened
 */
export async function openDocument(documentId, options = {}) {
  const token = localStorage.getItem('token');
  const res = await axios.get(apiUrl(`/api/document/${documentId}/link`), {
    headers: { Authorization: `Bearer ${token}` },
  });

  const path = options.download ? res.data?.download_url : res.data?.url;
  if (!path) throw new Error('Could not create a link for this document.');

  // Open synchronously-ish; popup blockers are avoided because this runs from a
  // user-initiated click handler.
  const win = window.open(apiUrl(path), '_blank', 'noopener');
  if (win) return true;

  // Popup blocked. A download can safely be handed to the current tab — the
  // response is an attachment, so the browser saves it and stays put. A view
  // cannot: navigating to it replaces the app and loses whatever the person
  // was doing, which is worse than not opening at all.
  if (options.download) {
    window.location.href = apiUrl(path);
    return true;
  }
  throw new Error('Your browser blocked the document window. Allow pop-ups for this site, or use Download.');
}

/**
 * Fetch a document's bytes and wrap them in an object URL, for callers that
 * render it inside the page.
 *
 * A signed /view URL cannot be framed here: the API sends
 * X-Frame-Options: SAMEORIGIN, so an <iframe> pointed at it is blocked
 * whenever the API is served from a different origin than the app (any split
 * dev setup). A blob: URL belongs to the page itself, so it always renders.
 *
 * The caller owns the returned URL and must URL.revokeObjectURL it.
 *
 * @param {number|string} documentId
 * @returns {Promise<string>} an object URL
 */
export async function fetchDocumentBlobUrl(documentId) {
  const token = localStorage.getItem('token');
  const res = await axios.get(apiUrl(`/api/document/${documentId}/view`), {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  return URL.createObjectURL(res.data);
}

/** Convenience wrapper for a save-as instead of an inline view. */
export function downloadDocument(documentId) {
  return openDocument(documentId, { download: true });
}
